import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { BarChart2, Globe, Share2, Mail, Megaphone, RefreshCw, Download, TrendingUp, TrendingDown, DollarSign, MousePointer, Users, FileText, Loader2, Plus } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend } from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { Acronym } from '../components/shared/AcronymTooltip';
import ModuleAutomationsPanel from '../components/automation/ModuleAutomationsPanel';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import { useCurrency } from '../contexts/CurrencyContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#10B981', '#3B82F6', '#8B5CF6', '#F59E0B'];

export const AnalyticsPage = () => {
  const { symbol: currSymbol } = useCurrency();
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [overviewRes, trendsRes] = await Promise.all([
        axios.get(`${API_URL}/api/analytics/overview`),
        axios.get(`${API_URL}/api/analytics/trends`),
      ]);
      setOverview(overviewRes.data);
      setTrends(trendsRes.data.data || []);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, trend, icon: Icon, prefix = '' }) => {
    const isPositive = trend >= 0;
    return (
      <Card className="bg-white border-gray-border">
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-emerald-brand/10 flex items-center justify-center">
              <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-brand" />
            </div>
            {trend !== undefined && (
              <div className={cn("flex items-center gap-1 text-xs sm:text-sm", isPositive ? "text-emerald-brand" : "text-red-500")}>
                {isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {Math.abs(trend)}%
              </div>
            )}
          </div>
          <p className="text-lg sm:text-2xl font-heading font-bold text-text-primary">{prefix}{typeof value === 'number' ? value.toLocaleString() : value}</p>
          <p className="text-xs sm:text-sm text-gray-medium">{title}</p>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 text-emerald-brand animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="analytics-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">Analytics & Reports<PageInfoTooltip pageKey="analytics" /></h1>
          <p className="text-gray-medium">Track your marketing performance across all channels</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">7d</Button>
          <Button variant="outline" size="sm" className="bg-emerald-brand/10 text-emerald-brand border-emerald-brand">30d</Button>
          <Button variant="outline" size="sm">90d</Button>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard title="Total Impressions" value={overview?.total_impressions || 0} icon={BarChart2} trend={12} />
        <StatCard title="Total Clicks" value={overview?.total_clicks || 0} icon={MousePointer} trend={8} />
        <StatCard title="Total Leads" value={overview?.total_leads || 0} icon={Users} trend={15} />
        <StatCard title="Total Revenue" value={overview?.total_revenue || 0} icon={DollarSign} prefix={currSymbol} trend={22} />
        <StatCard title="Avg Engagement" value={`${overview?.avg_engagement_rate || 0}%`} icon={Share2} />
        <StatCard title={<><Acronym term="ROAS" /></>} value={`${overview?.roas || 0}x`} icon={TrendingUp} />
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="bg-white border border-gray-border w-full sm:w-auto overflow-x-auto flex-nowrap justify-start">
          <TabsTrigger value="overview" className="text-xs sm:text-sm whitespace-nowrap">Overview</TabsTrigger>
          <TabsTrigger value="website" className="text-xs sm:text-sm whitespace-nowrap">Website</TabsTrigger>
          <TabsTrigger value="social" className="text-xs sm:text-sm whitespace-nowrap">Social</TabsTrigger>
          <TabsTrigger value="email" className="text-xs sm:text-sm whitespace-nowrap">Email</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports" className="text-xs sm:text-sm whitespace-nowrap">Custom Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Trends Chart */}
            <Card className="bg-white border-gray-border">
              <CardHeader>
                <CardTitle className="text-lg font-heading">Performance Trends</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trends}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#6B7280" />
                      <YAxis tick={{ fontSize: 12 }} stroke="#6B7280" />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="traffic" stroke="#10B981" strokeWidth={2} name="Website" dot={false} />
                      <Line type="monotone" dataKey="social" stroke="#3B82F6" strokeWidth={2} name="Social" dot={false} />
                      <Line type="monotone" dataKey="email" stroke="#8B5CF6" strokeWidth={2} name="Email" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Channel Performance */}
            <Card className="bg-white border-gray-border">
              <CardHeader>
                <CardTitle className="text-lg font-heading">Channel Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={overview?.channel_performance || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="channel" tick={{ fontSize: 12 }} stroke="#6B7280" />
                      <YAxis tick={{ fontSize: 12 }} stroke="#6B7280" />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="impressions" fill="#10B981" name="Impressions" />
                      <Bar dataKey="clicks" fill="#3B82F6" name="Clicks" />
                      <Bar dataKey="leads" fill="#8B5CF6" name="Leads" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="website">
          <Card className="bg-white border-gray-border">
            <CardContent className="p-8 text-center">
              <Globe className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-medium">Website analytics coming soon</p>
              <p className="text-sm text-gray-medium">Connect Google Analytics to see detailed website metrics</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="social">
          <Card className="bg-white border-gray-border">
            <CardContent className="p-8 text-center">
              <Share2 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-medium">Social analytics coming soon</p>
              <p className="text-sm text-gray-medium">Connect social accounts to see engagement metrics</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="email">
          <Card className="bg-white border-gray-border">
            <CardContent className="p-8 text-center">
              <Mail className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-medium">Email analytics coming soon</p>
              <p className="text-sm text-gray-medium">Send campaigns to see open and click rates</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reports" className="space-y-4">
          <CustomReportsBuilder />
        </TabsContent>
      </Tabs>
    </div>
  );
};

const CustomReportsBuilder = () => {
  const [reports, setReports] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [reportType, setReportType] = useState('overview');
  const [dateRange, setDateRange] = useState('last_30_days');

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/reports/saved`);
        setReports(res.data.reports || []);
      } catch (err) { console.error(err); }
    };
    fetchReports();
  }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API_URL}/api/reports/generate`, { type: reportType, date_range: dateRange });
      setReports(prev => [res.data.report, ...prev]);
      toast.success('Report generated!');
    } catch (err) { toast.error('Failed to generate report'); }
    finally { setGenerating(false); }
  };

  const reportTypeLabels = {
    overview: 'Overall Performance',
    social_performance: 'Social Media Performance',
    email_performance: 'Email Campaign Performance',
    seo_performance: 'SEO Performance'
  };

  return (
    <div className="space-y-4" data-testid="custom-reports-builder">
      <Card className="bg-white border-gray-border">
        <CardContent className="p-5">
          <div className="flex flex-col sm:flex-row gap-3 items-end">
            <div className="flex-1">
              <p className="text-sm font-medium text-text-primary mb-2">Report Type</p>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger data-testid="report-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="overview">Overall Performance</SelectItem>
                  <SelectItem value="social_performance">Social Media Performance</SelectItem>
                  <SelectItem value="email_performance">Email Campaign Performance</SelectItem>
                  <SelectItem value="seo_performance">SEO Performance</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-text-primary mb-2">Date Range</p>
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="last_7_days">Last 7 Days</SelectItem>
                  <SelectItem value="last_30_days">Last 30 Days</SelectItem>
                  <SelectItem value="last_90_days">Last 90 Days</SelectItem>
                  <SelectItem value="this_year">This Year</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button className="bg-emerald-brand hover:bg-emerald-hover gap-2" onClick={handleGenerate} disabled={generating} data-testid="generate-report-btn">
              {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Generate Report
            </Button>
          </div>
        </CardContent>
      </Card>

      {reports.map((report, i) => (
        <Card key={report.id || i} className="bg-white border-gray-border" data-testid={`report-card-${report.id}`}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-medium text-text-primary">{reportTypeLabels[report.type] || report.type}</h3>
                <p className="text-xs text-gray-medium">{new Date(report.generated_at).toLocaleString()} | {report.date_range?.replace(/_/g, ' ')}</p>
              </div>
              <Badge className="bg-emerald-brand text-white">Generated</Badge>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(report.data || {}).map(([key, value]) => (
                <div key={key} className="p-3 bg-off-white rounded-lg">
                  <p className="text-xs text-gray-medium capitalize">{key.replace(/_/g, ' ')}</p>
                  <p className="text-lg font-semibold text-text-primary">
                    {Array.isArray(value) ? value.join(', ') : typeof value === 'number' && value > 100 ? value.toLocaleString() : String(value)}
                    {key.includes('rate') ? '%' : ''}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}

      {reports.length === 0 && (
        <Card className="bg-white border-gray-border">
          <CardContent className="p-8 text-center">
            <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-medium mb-1">No reports generated yet</p>
            <p className="text-sm text-gray-medium">Select a report type and generate your first custom report</p>
          </CardContent>
        </Card>
      )}
      <ModuleAutomationsPanel module="analytics" title="Analytics Automations" />
    </div>
  );
};

export default AnalyticsPage;
